// v149 — Quick Promote helpers shared by the wizard, the advertiser pages,
// the publisher earnings tab and the public `ads` block.
import { Megaphone, ShoppingBag, Ticket, PlayCircle } from "lucide-react";
import { api } from "./api";

export const CONTENT_TYPES = [
  { key: "bio_page", label: "Web Link", hint: "Your page card with followers & likes", icon: Megaphone },
  { key: "product", label: "Product / Service / Event", hint: "Anything from your store", icon: ShoppingBag },
  { key: "paid_coupon", label: "Paid coupon", hint: "One of your paid coupons or vouchers", icon: Ticket },
  { key: "video", label: "Video", hint: "Instagram Reel or YouTube Short", icon: PlayCircle },
];

export const contentTypeLabel = (key) =>
  (CONTENT_TYPES.find((t) => t.key === key) || {}).label || "Promotion";

export const STATUS_META = {
  active: { label: "Live", tone: "success" },
  paused: { label: "Paused", tone: "neutral" },
  exhausted: { label: "Budget used up", tone: "warning" },
  suspended: { label: "Suspended", tone: "danger" },
  deleted: { label: "Deleted", tone: "neutral" },
};

// v149.75 — The signed-in account's billing currency (INR for India, USD
// elsewhere; see backend/currency_service.py). AuthContext sets it from
// `/auth/me`; every amount a page shows with `inr()` is already in that
// currency, so the formatter only decides the symbol and grouping. The name
// is historical (it predates USD) and is kept so no call site changes.
let BILLING_CURRENCY = "INR";
export const setBillingCurrency = (code) => { BILLING_CURRENCY = String(code || "INR").toUpperCase() === "USD" ? "USD" : "INR"; };
export const getBillingCurrency = () => BILLING_CURRENCY;

export const money = (n, currency, { decimals } = {}) => {
  const cur = String(currency || BILLING_CURRENCY).toUpperCase();
  const v = Number(n || 0);
  if (cur === "USD") {
    const d = decimals ?? 2;
    return `$${v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
  }
  const d = decimals ?? (Number.isInteger(v) ? 0 : 2);
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};
export const inr = (n, opts = {}) => money(n, opts.currency, opts);
export const currencySymbol = (currency) => (String(currency || BILLING_CURRENCY).toUpperCase() === "USD" ? "$" : "₹");

export const count = (n) => Number(n || 0).toLocaleString("en-IN");

// The three chargeable actions. Views (video plays) only exist on video ads.
export const CHARGE_EVENTS = [
  { key: "impression", rateKey: "impression_rate_inr", statKey: "impressions", label: "impression", title: "Impressions", short: "Impr." },
  { key: "play", rateKey: "play_rate_inr", statKey: "plays", label: "view", title: "Views", short: "Views" },
  { key: "click", rateKey: "click_rate_inr", statKey: "clicks", label: "click", title: "Clicks", short: "Clicks" },
];

export const chargeEventsFor = (contentType) =>
  CHARGE_EVENTS.filter((e) => e.key !== "play" || contentType === "video");

export const HIGHEST_PRICE_RULE =
  "Each visitor is charged once per ad per Web Link — at the highest price among the impression, view and click they made, never the sum.";

// Visitors charged at each price level (highest price first) for a stats
// object from the API (`charged_impression` / `charged_play` / `charged_click`).
export function chargeBreakdown(stats, pricing, contentType) {
  return chargeEventsFor(contentType)
    .map((e) => {
      const visitors = Number(stats?.[`charged_${e.key}`] || 0);
      const rate = Number(pricing?.[e.rateKey] || 0);
      return { ...e, visitors, rate, amount: Math.round(visitors * rate * 100) / 100 };
    })
    .filter((r) => r.visitors > 0)
    .sort((a, b) => b.rate - a.rate);
}

// "1 click" / "2 clicks"
export const plural = (n, one, many = `${one}s`) => `${count(n)} ${Number(n) === 1 ? one : many}`;

export function targetSummary(target) {
  if (!target) return "";
  if (target.mode === "city") {
    // v149.10 — Multi-city. Two names fit on a phone; beyond that we count
    // the rest so the row stays one line instead of wrapping into the
    // next card. Falls back to the legacy single `location_name` for ads
    // saved before multi-city, which never carry `location_names`.
    const names = (target.location_names || []).filter(Boolean);
    if (names.length > 2) return `${names[0]}, ${names[1]} +${names.length - 2} more`;
    if (names.length) return names.join(", ");
    return target.location_name || "Selected city";
  }
  const km = Number(target.radius_km || 0);
  const place = (target.address || "").split(",").slice(0, 2).join(",").trim();
  return `${km} km around ${place || "the pinned point"}`;
}

// Where an ad's target came from (set by the server when the ad is saved).
const TARGET_SOURCES = {
  business_location: "Business location from Settings",
  business_city: "City from Settings",
  custom: "Custom location",
};
export const targetSourceLabel = (target) => TARGET_SOURCES[target?.source] || null;

export const coordsLabel = (lat, lng) => (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)) && lat !== null && lng !== null
  ? `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`
  : "");

export function distanceKm(a, b) {
  if (!a || !b || [a.lat, a.lng, b.lat, b.lng].some((v) => v === null || v === undefined || !Number.isFinite(Number(v)))) return null;
  const rad = (d) => (Number(d) * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

// "1.2 km" / "850 m"
export const distanceLabel = (km) => {
  const v = Number(km);
  if (km === null || km === undefined || !Number.isFinite(v)) return "";
  return v < 1 ? `${Math.round(v * 1000)} m` : `${v.toFixed(1)} km`;
};

// ---- Smart QR attribution ----------------------------------------------------
// A Smart QR scan redirects to /u/{slug}?qr={code}. The code is remembered for
// that bio page for the rest of the visit (sessionStorage, 12 h), so ad
// impressions / views / clicks are reported against the QR that was scanned —
// also after moving between the owner's pages or reloading.
const QR_KEY = "linkola:qp_qr:";
const QR_TTL_MS = 12 * 60 * 60 * 1000;
const QR_RE = /^[A-Z0-9]{6,12}$/;

export function rememberQrVisit(slug) {
  if (!slug || typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const code = (url.searchParams.get("qr") || "").trim().toUpperCase();
    if (!code) return;
    if (QR_RE.test(code)) {
      sessionStorage.setItem(`${QR_KEY}${slug.toLowerCase()}`, JSON.stringify({ code, at: Date.now() }));
    }
    // Keep shared / copied links free of the scan code.
    url.searchParams.delete("qr");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  } catch { /* ignore */ }
}

export function getVisitQr(slug) {
  if (!slug) return null;
  rememberQrVisit(slug);
  try {
    const raw = JSON.parse(sessionStorage.getItem(`${QR_KEY}${slug.toLowerCase()}`) || "null");
    return raw && QR_RE.test(raw.code) && Date.now() - Number(raw.at || 0) < QR_TTL_MS ? raw.code : null;
  } catch {
    return null;
  }
}

// Stable per-browser device id for ad impression / view / click de-duplication.
const DEVICE_KEY = "linkola:qp_device";
export function getAdDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
      const rand = (window.crypto?.randomUUID?.() || `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`)
        .replace(/[^A-Za-z0-9]/g, "");
      id = `qpd_${rand.slice(0, 32)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

// Fire-and-forget impression / play (view) / click tracking. Never throws.
export function trackAdEvent({ adId, slug, pageId, event }) {
  if (!adId || !slug) return;
  api.post("/public/quick-promote/events", {
    ad_id: adId,
    slug,
    page_id: pageId || undefined,
    event,
    device_id: getAdDeviceId() || undefined,
    qr_code: getVisitQr(slug) || undefined,
  }).catch(() => {});
}

// The ads block always renders after every other block on a page.
export function orderAdsLast(blocks) {
  const list = Array.isArray(blocks) ? blocks : [];
  if (!list.some((b) => b?.type === "ads")) return list;
  return [...list.filter((b) => b?.type !== "ads"), ...list.filter((b) => b?.type === "ads")];
}

export const isInsufficientBalance = (err) =>
  err?.response?.status === 402 && err?.response?.data?.detail?.code === "insufficient_balance";

export const errorMessage = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d.message === "string") return d.message;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

// In-app viewers (/app/view/u/...) keep users inside the shell.
export function bioHref(slug) {
  if (!slug) return "#";
  const inApp = typeof window !== "undefined" && window.location.pathname.startsWith("/app/");
  return inApp
    ? `/app/view/u/${slug}?back=${encodeURIComponent(window.location.pathname)}`
    : `/u/${slug}`;
}
